import React from "react";
import BaseEdge, { roundedOrthPath, roundTo } from "./BaseEdge";

/**
 * EntityRelationEdge
 * -------------------
 * MaxGraph equivalent: mxEdgeStyle.EntityRelation
 *
 * Behavior:
 *  - Orthogonal elbow routing with *extra spacing* between cp1 and cp2.
 *  - Produces ER-style connecting lines that look cleaner and wider.
 *
 * Routing shape:
 *
 *   src ---- cp1
 *             |
 *             |
 *           cp2 ---- dst
 *
 *  Default spacing: 60px (can adjust)
 */

const ER_SPACING = 60; // extra horizontal/vertical separation for ER flow

function chooseERPorts(srcNode, dstNode) {
  const sx = srcNode.x + srcNode.width / 2;
  const sy = srcNode.y + srcNode.height / 2;
  const tx = dstNode.x + dstNode.width / 2;
  const ty = dstNode.y + dstNode.height / 2;

  const dx = tx - sx;
  const dy = ty - sy;

  // choose dominant direction
  if (Math.abs(dx) > Math.abs(dy)) {
    return {
      fromPort: dx > 0 ? "right" : "left",
      toPort: dx > 0 ? "left" : "right"
    };
  }

  return {
    fromPort: dy > 0 ? "bottom" : "top",
    toPort: dy > 0 ? "top" : "bottom"
  };
}

function anchorPoint(node, port) {
  const w = node.width;
  const h = node.height;

  switch (port) {
    case "left": return { x: roundTo(node.x), y: roundTo(node.y + h / 2) };
    case "right": return { x: roundTo(node.x + w), y: roundTo(node.y + h / 2) };
    case "top": return { x: roundTo(node.x + w / 2), y: roundTo(node.y) };
    case "bottom": return { x: roundTo(node.x + w / 2), y: roundTo(node.y + h) };
    default: return { x: roundTo(node.x + w / 2), y: roundTo(node.y + h / 2) };
  }
}

export default function EntityRelationEdge({
  edge,
  nodeById,
  edgeIndex,
  selectedIndex,
  onSelectEdge,
  onStartEdgeDrag,
  edgeControls,
  color,

  // Hover props
  isHovered,
  onHoverEnter,
  onHoverLeave
}) {
  const fromNode = nodeById[edge.from];
  const toNode = nodeById[edge.to];
  if (!fromNode || !toNode) return null;

  // Determine ports
  const ports = chooseERPorts(fromNode, toNode);
  const src = anchorPoint(fromNode, ports.fromPort);
  const dst = anchorPoint(toNode, ports.toPort);

  // Default "expanded" ER control points
  const ctrl = edgeControls?.[edgeIndex];

  let cp1, cp2;

  if (ports.fromPort === "right" || ports.fromPort === "left") {
    // Horizontal first → then vertical
    const midX =
      ports.fromPort === "right"
        ? src.x + ER_SPACING
        : src.x - ER_SPACING;

    const midX2 =
      ports.toPort === "left"
        ? dst.x - ER_SPACING
        : dst.x + ER_SPACING;

    cp1 = ctrl?.cp1 ?? { x: midX, y: src.y };
    cp2 = ctrl?.cp2 ?? { x: midX2, y: dst.y };
  } else {
    // Vertical first → then horizontal
    const midY =
      ports.fromPort === "bottom"
        ? src.y + ER_SPACING
        : src.y - ER_SPACING;

    const midY2 =
      ports.toPort === "top"
        ? dst.y - ER_SPACING
        : dst.y + ER_SPACING;

    cp1 = ctrl?.cp1 ?? { x: src.x, y: midY };
    cp2 = ctrl?.cp2 ?? { x: dst.x, y: midY2 };
  }

  const pts = [src, cp1, cp2, dst];
  const d = roundedOrthPath(pts);

  const labelPos = {
    x: Math.round((cp1.x + cp2.x) / 2),
    y: Math.round((cp1.y + cp2.y) / 2) - 12
  };

  return (
    <BaseEdge
      d={d}
      stroke={color}

      isHovered={isHovered}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}

      onSelect={() => onSelectEdge(edgeIndex)}
      onStartDrag={(e) =>
        onStartEdgeDrag(edgeIndex, {
          clientX: e.clientX,
          clientY: e.clientY
        })
      }

      label={edge.label}
      labelPos={labelPos}
    />
  );
}
